import React from 'react';
import { Box, Typography, Stack, Chip } from '@mui/joy';

const RecordingTimeline = ({ recordings = [] }) => {
    // Get 24 hours ago and now
    const now = Date.now();
    const twentyFourHoursAgo = now - (24 * 60 * 60 * 1000);
    
    // Process recordings to create coverage segments
    const processRecordings = () => {
        if (!recordings || recordings.length === 0) {
            return [];
        }
        
        // Filter recordings within last 24 hours and sort by start time
        const validRecordings = recordings
            .filter(rec => rec.start_ms >= twentyFourHoursAgo && rec.start_ms <= now)
            .sort((a, b) => a.start_ms - b.start_ms);
            
        return validRecordings.map(rec => ({
            start: rec.start_ms,
            end: rec.end_ms || rec.start_ms + (rec.duration_ms || 0),
            duration: rec.duration_ms || 0,
            size: rec.size_bytes || 0,
            codec: rec.codec || 'unknown'
        }));
    };
    
    const segments = processRecordings();
    
    // Debug logging
    console.log('RecordingTimeline Debug:', {
        recordings: recordings?.length || 0,
        segments: segments.length,
        twentyFourHoursAgo: new Date(twentyFourHoursAgo).toLocaleString(),
        now: new Date(now).toLocaleString(),
        segmentDetails: segments.map(s => ({
            start: new Date(s.start).toLocaleString(),
            end: new Date(s.end).toLocaleString(),
            duration: s.duration
        }))
    });
    
    // Calculate coverage percentage for each segment
    const getSegmentPosition = (timestamp) => {
        return ((timestamp - twentyFourHoursAgo) / (24 * 60 * 60 * 1000)) * 100;
    };
    
    // Generate time labels (every 4 hours)
    const getTimeLabels = () => {
        const labels = [];
        for (let i = 0; i <= 6; i++) {
            const time = new Date(twentyFourHoursAgo + (i * 4 * 60 * 60 * 1000));
            labels.push({
                position: (i / 6) * 100,
                label: time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
            });
        }
        return labels;
    };
    
    const timeLabels = getTimeLabels();
    
    // Calculate total coverage
    const totalCoverage = segments.reduce((acc, seg) => {
        return acc + (seg.end - seg.start);
    }, 0);
    
    const coverageHours = (totalCoverage / (1000 * 60 * 60)).toFixed(1);
    const coveragePercent = ((totalCoverage / (24 * 60 * 60 * 1000)) * 100).toFixed(1);
    
    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography level="body-xs" fontWeight="bold" textColor="neutral.600">
                    24h Recording Coverage
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Chip size="sm" variant="soft" color={segments.length > 0 ? "success" : "neutral"}>
                        {segments.length} recordings
                    </Chip>
                    <Chip size="sm" variant="outlined" color="primary">
                        {coverageHours}h ({coveragePercent}%)
                    </Chip>
                </Stack>
            </Stack>
            
            {/* Timeline container */}
            <Box sx={{ 
                position: 'relative', 
                height: '60px', 
                mb: 1,
                backgroundColor: 'neutral.50',
                borderRadius: '8px',
                p: 1,
                border: '1px solid',
                borderColor: 'neutral.300',
                minWidth: '100%'
            }}>
                {/* Background timeline */}
                <Box sx={{
                    position: 'absolute',
                    top: '16px',
                    left: '8px',
                    right: '8px',
                    height: '20px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '10px',
                    border: '2px solid #d1d5db',
                    zIndex: 1
                }} />
                
                {/* Recording segments */}
                {segments.map((segment, index) => {
                    const left = getSegmentPosition(segment.start);
                    const width = getSegmentPosition(segment.end) - left;
                    const adjustedLeft = Math.max(0, Math.min(left, 100));
                    const adjustedWidth = Math.max(0.5, Math.min(width, 100 - adjustedLeft)); // Minimum 0.5% width
                    
                    console.log(`Segment ${index}:`, { 
                        start: new Date(segment.start).toLocaleTimeString(),
                        end: new Date(segment.end).toLocaleTimeString(),
                        left, 
                        width, 
                        adjustedLeft, 
                        adjustedWidth 
                    });
                    
                    return (
                        <Box
                            key={index}
                            sx={{
                                position: 'absolute',
                                top: '20px', // Centered in smaller gray area + moved down 10% of green height (1.4px)
                                left: `${8 + (adjustedLeft * (100 - 16) / 100)}%`,
                                width: `${adjustedWidth * (100 - 16) / 100}%`,
                                height: '14px',
                                backgroundColor: '#22c55e', // Direct green color
                                borderRadius: '10px', // Larger radius for more rounded appearance
                                border: '1px solid #16a34a', // Thinner border for better centering
                                zIndex: 10,
                                cursor: 'pointer',
                                boxSizing: 'border-box',
                                minWidth: '2px', // Ensure minimum visibility
                                '&:hover': {
                                    backgroundColor: '#16a34a',
                                    transform: 'scaleY(1.05)',
                                }
                            }}
                            title={`Recording: ${new Date(segment.start).toLocaleTimeString()} - ${new Date(segment.end).toLocaleTimeString()}\nDuration: ${(segment.duration / 1000 / 60).toFixed(1)} min\nSize: ${(segment.size / 1024 / 1024).toFixed(1)} MB`}
                        />
                    );
                })}
                
                {/* Time markers */}
                {timeLabels.map((label, index) => (
                    <Box
                        key={index}
                        sx={{
                            position: 'absolute',
                            left: `${8 + (label.position * (100 - 16) / 100)}%`,
                            top: '38px', // Below the reduced gray area
                            transform: 'translateX(-50%)',
                            width: '1px',
                            height: '8px',
                            backgroundColor: '#9ca3af',
                            borderRadius: '0.5px',
                            zIndex: 5
                        }}
                    />
                ))}
                
                {/* Time labels inside container */}
                {timeLabels.map((label, index) => (
                    <Typography
                        key={`label-${index}`}
                        level="body-xs"
                        textColor="neutral.500"
                        sx={{ 
                            position: 'absolute',
                            left: `${8 + (label.position * (100 - 16) / 100)}%`,
                            top: '48px', // Below the time markers
                            transform: 'translateX(-50%)',
                            fontSize: '10px',
                            textAlign: 'center',
                            minWidth: '35px',
                            zIndex: 5
                        }}
                    >
                        {label.label}
                    </Typography>
                ))}
            </Box>
            

            

        </Box>
    );
};

export default RecordingTimeline; 